import { DrawingManager, LinkHandlingMode, ModelRenderer, RenderDrawingToManager } from '@cadstrom/renderer';
import clsx from 'clsx';
import { memo, use, useCallback, useEffect, useRef } from 'react';
import invariant from 'tiny-invariant';

import { CadRendererSetActiveDesignEvent, CadRendererViewportChangedEvent } from '#src/utils/events.js';
import { ZOOM_PADDING_FRACTION } from './constants.js';
import { CadstromColorProviderContext } from './context/cadstrom-color-provider-context.js';
import { useDrawingManagerContext } from './context/drawing-manager-context.js';
import { useInteractionManagerContext } from './context/interaction-manager-context.js';
import { resolveZoomSelection } from './module-zoom-selection.js';

import type { DrawingLink, IModelRenderOpts } from '@cadstrom/renderer';
import type { Design } from '@cadstrom/sigma-engine-shared';
import type { RefCallback } from 'react';
import type { CadRendererZoomEvent } from '#src/utils/events.js';
import type { CoreModelWithDesignSummaries } from './context/core-model-with-design-summaries.js';

export type CadRendererCanvasProps = {
	revisionId: string;
	coreModelWithDesignSummaries: CoreModelWithDesignSummaries;
	design: Design;
	onInitialized?: (drawingManager: DrawingManager) => void;
	multiInstanceId?: string;
	imageUrlResolver?: (imageRef: string) => string | Promise<string>;
	/**
	 * Whether this canvas is the currently visible (active) sheet. Only the active canvas should
	 * handle zoom events — background canvases share the same listener and would otherwise race
	 * to write to the global interaction manager, leaving the selection keyed to the wrong sheet.
	 */
	isActive?: boolean;
};

export const CadRendererCanvas = memo(function CadRendererCanvas({
	revisionId,
	coreModelWithDesignSummaries,
	design,
	onInitialized,
	multiInstanceId,
	imageUrlResolver,
	isActive,
}: CadRendererCanvasProps) {
	const { interactionManager } = useInteractionManagerContext();
	const { drawingManager, setDrawingManager } = useDrawingManagerContext();

	const containerRef = useRef<HTMLDivElement | null>(null);

	const colorProvider = use(CadstromColorProviderContext)?.byLayerSet.get(design.layerSet);
	invariant(
		colorProvider,
		`No colour provider for layer set "${design.layerSet}" — mount CadstromColorProviderContextProvider under the CoreModelContext that holds this design's model.`,
	);
	const canvasBackground = colorProvider.getCanvasBackground();

	useEffect(() => {
		const handleCadRendererZoom = (event: CadRendererZoomEvent) => {
			if (!isActive || !drawingManager || drawingManager.isDestroyed) {
				return;
			}

			// A signal/pin/pad yields a partial selection (single pin/pad, or a signal's pins/pads); a bare
			// reference designator selects the whole module(s). The first command replaces the selection, the
			// rest extend it, so a multi-pin signal ends up fully selected.
			const commands = resolveZoomSelection(event.detail, design, coreModelWithDesignSummaries.components);
			if (commands.length === 0) {
				return;
			}
			for (const [index, command] of commands.entries()) {
				interactionManager.selectElementsByProperty(command.property, command.value, index > 0);
			}

			drawingManager.zoomToSelection(ZOOM_PADDING_FRACTION);
		};

		addEventListener('cadRendererZoom', handleCadRendererZoom);
		return () => removeEventListener('cadRendererZoom', handleCadRendererZoom);
	});

	const handleCanvasRef: RefCallback<HTMLCanvasElement> = useCallback(
		(canvas) => {
			if (!canvas) {
				return;
			}

			const layers = coreModelWithDesignSummaries.layerSets[design.layerSet];
			invariant(layers, `Layer set "${design.layerSet}" not found in model.`);

			const modelRenderer = new ModelRenderer(canvas, design.edges_bbox, design.initial_zoombox, {
				bounceExpandPercent: 10,
				enableClamp: true,
				zoomClampRatio: 4,
				dragOpts: {
					mouseButtons: 'middle-right',
				},
				wheelOpts: {
					trackpadPinch: true,
					wheelZoom: false,
				},
			});
			modelRenderer.init();

			const drawingManager = new DrawingManager(modelRenderer, colorProvider, interactionManager, layers, {
				enableDoubleClick: true,
			});

			// Enable double-click navigation for schematic references
			drawingManager.linkHandlingMode = LinkHandlingMode.DoubleClick;

			// Subscribe to link click events for navigation
			const handleLinkClicked = (link: DrawingLink) => {
				dispatchEvent(new CadRendererSetActiveDesignEvent({ designId: link.designId }));
			};
			drawingManager.on('linkClicked', handleLinkClicked);

			// Broadcast viewport state changes for selection history tracking.
			// Coalesced to one dispatch per animation frame — viewportMoved/viewportZoomed
			// fire at pointer-event frequency which would spam downstream listeners.
			let rafId: ReturnType<typeof requestAnimationFrame> | null = null;
			const handleViewportEvent = () => {
				if (rafId !== null) {
					return;
				}
				rafId = requestAnimationFrame(() => {
					rafId = null;
					const viewport = modelRenderer.getViewport();
					dispatchEvent(
						new CadRendererViewportChangedEvent({
							revisionId,
							viewport: {
								centerX: viewport.center.x,
								centerY: viewport.center.y,
								scale: viewport.scaled,
							},
						}),
					);
				});
			};
			modelRenderer.on('viewportMoved', handleViewportEvent);
			modelRenderer.on('viewportZoomed', handleViewportEvent);

			const renderOpts: IModelRenderOpts = {
				// Generate opts required for BOM/interactivity
				generateOpts: true,

				// TODO:
				generatePadText: true,

				multiInstanceId,
				imageUrlResolver,
			};

			RenderDrawingToManager(drawingManager, coreModelWithDesignSummaries, design, renderOpts);

			setDrawingManager(drawingManager);
			for (const warning of drawingManager.warnings) {
				console.warn(`Render warning: ${warning}`);
			}

			return () => {
				if (rafId !== null) {
					cancelAnimationFrame(rafId);
				}
				drawingManager.removeAllListeners();
				drawingManager.destroy();
				modelRenderer.destroy();
				setDrawingManager(null);
			};
		},
		[
			coreModelWithDesignSummaries,
			design,
			colorProvider,
			interactionManager,
			multiInstanceId,
			imageUrlResolver,
			setDrawingManager,
			revisionId,
		],
	);

	const hasReportedInitialization = useRef(false);
	useEffect(() => {
		if (drawingManager && !drawingManager.isDestroyed && !hasReportedInitialization.current) {
			onInitialized?.(drawingManager);
			hasReportedInitialization.current = true;
		}
	}, [drawingManager, onInitialized]);

	return (
		<div
			ref={containerRef}
			className={clsx('relative m-0 h-full w-full max-w-full grow', canvasBackground === undefined && 'bg-gray-2')}
			style={canvasBackground === undefined ? undefined : { backgroundColor: canvasBackground }}
		>
			<canvas
				ref={handleCanvasRef}
				className='absolute top-0 left-0 h-full w-full'
				onContextMenu={(e) => e.preventDefault()}
			/>
		</div>
	);
});
